// The opt-in depth tier: a slide-in dock with the full inspector + layers list. On
// desktop it's a left column of the composer center so the chat rail stays put; on
// mobile (where a 300px column won't fit) it's a bottom sheet over the canvas. Hidden
// by default; opened via the bar's Layers toggle or auto-opened on a deep edit.

import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useIsMobile } from 'hooks/use-mobile'
import { Inspector } from './Inspector'
import { LayersPanel } from './LayersPanel'
import { useComposerEditor } from './CompositionEditorProvider'

export function CompositionDock() {
  const ctx = useComposerEditor()
  const isMobile = useIsMobile()
  if (!ctx || !ctx.dock.open) return null
  const { editor, dock } = ctx

  const panel = (
    <>
      <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
        <span className="text-xs font-semibold text-paper">Design</span>
        <button
          type="button"
          onClick={() => dock.setOpen(false)}
          className="text-paper-dim hover:text-paper"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {/* Layers on top (constant across layer types), then the contextual inspector for the
          focused layer below — so the always-present list stays put while the per-layer controls
          change with selection. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <LayersPanel editor={editor} />
        <div className="border-t border-line" />
        <Inspector editor={editor} />
      </div>
    </>
  )

  // Mobile: the dock is a bottom sheet that must sit ABOVE the composer's edit sheets
  // (MobileBottomSheet portals to document.body at z-50). Rendering inline at z-50 lets
  // the later-in-DOM edit sheet paint over it, hiding the dock entirely — so we portal to
  // body too and stack above (z-[60]). Without this, the full Inspector/Layers (replace
  // photo, fit, reposition, fonts, etc.) is unreachable from the image editor on mobile.
  if (isMobile) {
    return createPortal(
      <>
        <div
          className="fixed inset-0 z-[59] bg-black/60 backdrop-blur-sm"
          onClick={() => dock.setOpen(false)}
          aria-hidden
        />
        <div className="fixed inset-x-0 bottom-0 z-[60] flex max-h-[72vh] flex-col overflow-hidden rounded-t-2xl border-t border-line bg-bg-2 pb-[env(safe-area-inset-bottom)]">
          {panel}
        </div>
      </>,
      document.body
    )
  }

  // Desktop: an inline static column to the left of the canvas.
  return (
    <div className="flex w-[300px] shrink-0 flex-col overflow-hidden border-r border-line bg-bg-2">
      {panel}
    </div>
  )
}
